import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './LandingPage.css';
import HeaderOne from '../Components/Header/HeaderOne';
import FooterOne from '../Components/Footer/FooterOne';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons for different types
const towerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const wifiIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const LandingPage = () => {
  const [towers, setTowers] = useState([]);
  const [wifiLocations, setWifiLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total_towers: 0, total_wifi: 0 });
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, towers, wifi
  const [showTowers, setShowTowers] = useState(true);
  const [showWifi, setShowWifi] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState([-8.61, 120.12]);
  const [mapZoom, setMapZoom] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKecamatan, setSelectedKecamatan] = useState('');
  const [kecamatanList, setKecamatanList] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState('');
  const [operatorList, setOperatorList] = useState([]);
  const [selectedJenis, setSelectedJenis] = useState('');
  const [jenisList, setJenisList] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('');
  const [providerList, setProviderList] = useState([]);
  const [selectedTowerStatus, setSelectedTowerStatus] = useState('');
  const [selectedWifiStatus, setSelectedWifiStatus] = useState('');
  const [selectedWifiKecamatan, setSelectedWifiKecamatan] = useState('');
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  const filteredTowers = towers.filter(tower =>
    showTowers &&
    (tower.nama_optel.toLowerCase().includes(searchTerm.toLowerCase()) ||
     tower.lokasi.toLowerCase().includes(searchTerm.toLowerCase()) ||
     tower.operator.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (selectedKecamatan === '' || tower.kecamatan === selectedKecamatan) &&
    (selectedOperator === '' || tower.operator === selectedOperator) &&
    (selectedJenis === '' || tower.jenis === selectedJenis) &&
    (selectedTowerStatus === '' || tower.status === selectedTowerStatus)
  );

  const filteredWifi = wifiLocations.filter(wifi =>
    showWifi &&
    (wifi.nama.toLowerCase().includes(searchTerm.toLowerCase()) ||
     wifi.lokasi.toLowerCase().includes(searchTerm.toLowerCase()) ||
     wifi.kecamatan.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (selectedWifiKecamatan === '' || wifi.kecamatan === selectedWifiKecamatan) &&
    (selectedProvider === '' || wifi.provider === selectedProvider) &&
    (selectedWifiStatus === '' || wifi.status === selectedWifiStatus)
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token'); // Assume token is stored
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

        const statsRes = await fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/stats');
        const statsData = await statsRes.json();
        setStats(statsData);

        const towersRes = await fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/towers', { headers });
        const wifiRes = await fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/wifi', { headers });
        const jenisRes = await fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/jenis', { headers });
        const operatorRes = await fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/operator', { headers });
        const kecamatanRes = await fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/kecamatan', { headers });
        const providerRes = await fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/provider', { headers });

        if (!towersRes.ok || !wifiRes.ok) throw new Error('Failed to fetch data');

        const towersData = await towersRes.json();
        const wifiData = await wifiRes.json();
        const jenisData = jenisRes.ok ? await jenisRes.json() : [];
        const operatorData = operatorRes.ok ? await operatorRes.json() : [];
        const kecamatanData = kecamatanRes.ok ? await kecamatanRes.json() : [];
        const providerData = providerRes.ok ? await providerRes.json() : [];

        setTowers(towersData);
        setWifiLocations(wifiData);
        setJenisList(jenisData);
        setOperatorList(operatorData);
        setKecamatanList(kecamatanData);
        setProviderList(providerData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpen && !event.target.closest('.menu-panel') && !event.target.closest('.menu-button')) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="landing-container">
      <HeaderOne />
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            {/* Elegant Centered Title and Subtitle */}
            <div className="text-center my-4">
              <h2 className="fw-bold mb-2" style={{letterSpacing: '1px', color: '#2c3e50'}}>
                KOORDITEL
              </h2>
              <div className="text-muted mb-3" style={{fontSize: '1.1rem'}}> Koordinat dan Data Informasi Telekomunikasi Berbasis GIS <span style={{color:'#007bff'}}>Interaktif</span> &amp; <span style={{color:'#28a745'}}>Statistik Real-Time</span> Kabupaten Manggarai Barat</div>
            </div>
            {/* Responsive 4 Statistic Cards */}
            <div className="stats-cards mb-4">
              <div className="stat-card stat-primary">
                <div className="stat-icon stat-primary"><i className="fas fa-broadcast-tower fa-lg"></i></div>
                <div className="stat-number">{stats.total_towers}</div>
                <div className="stat-label">Total Menara</div>
              </div>
              <div className="stat-card stat-success">
                <div className="stat-icon stat-success"><i className="fas fa-wifi fa-lg"></i></div>
                <div className="stat-number">{stats.total_wifi}</div>
                <div className="stat-label">Total WiFi</div>
              </div>
              <div className="stat-card stat-info">
                <div className="stat-icon stat-info"><i className="fas fa-broadcast-tower fa-lg"></i></div>
                <div className="stat-number">{towers.filter(t => t.status === 'Aktif').length}</div>
                <div className="stat-label">Menara Aktif</div>
              </div>
              <div className="stat-card stat-warning">
                <div className="stat-icon stat-warning"><i className="fas fa-wifi fa-lg"></i></div>
                <div className="stat-number">{wifiLocations.filter(w => w.status === 'Aktif').length}</div>
                <div className="stat-label">WiFi Aktif</div>
              </div>
            </div>
            <div className="map-card">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="fas fa-map-marked-alt"></i> Peta Interaktif Infrastruktur Telekomunikasi
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="map-container">
                  <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: '600px', width: '100%' }}>
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    {filteredTowers.map(tower => (
                      <Marker key={tower.id} position={[tower.koordinat_lat, tower.koordinat_lng]} icon={towerIcon}>
                        <Popup>
                          <div style={{ minWidth: '200px' }}>
                            <h6 className="text-primary mb-2">
                              <i className="fas fa-broadcast-tower"></i> {tower.nama_optel}
                            </h6>
                            <table className="table table-sm table-borderless">
                              <tbody>
                                <tr>
                                  <td><strong>Operator:</strong></td>
                                  <td>{tower.operator}</td>
                                </tr>
                                <tr>
                                  <td><strong>Lokasi:</strong></td>
                                  <td>{tower.lokasi}</td>
                                </tr>
                                <tr>
                                  <td><strong>Kecamatan:</strong></td>
                                  <td>{tower.kecamatan}</td>
                                </tr>
                                <tr>
                                  <td><strong>Status:</strong></td>
                                  <td>
                                    <span className={`badge ${tower.status === 'Aktif' ? 'bg-success' : 'bg-danger'}`}>
                                      {tower.status}
                                    </span>
                                  </td>
                                </tr>
                                <tr>
                                  <td><strong>Jenis:</strong></td>
                                  <td>{tower.jenis}</td>
                                </tr>
                                <tr>
                                  <td><strong>Tinggi:</strong></td>
                                  <td>{tower.tinggi} m</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                    {filteredWifi.map(wifi => (
                      <Marker key={wifi.id} position={[wifi.koordinat_lat, wifi.koordinat_lng]} icon={wifiIcon}>
                        <Popup>
                          <div style={{ minWidth: '200px' }}>
                            <h6 className="text-success mb-2">
                              <i className="fas fa-wifi"></i> {wifi.nama}
                            </h6>
                            <table className="table table-sm table-borderless">
                              <tbody>
                                <tr>
                                  <td><strong>Lokasi:</strong></td>
                                  <td>{wifi.lokasi}</td>
                                </tr>
                                <tr>
                                  <td><strong>Kecamatan:</strong></td>
                                  <td>{wifi.kecamatan}</td>
                                </tr>
                                <tr>
                                  <td><strong>Provider:</strong></td>
                                  <td>{wifi.provider}</td>
                                </tr>
                                <tr>
                                  <td><strong>Status:</strong></td>
                                  <td>
                                    <span className={`badge ${wifi.status === 'Aktif' ? 'bg-success' : 'bg-danger'}`}>
                                      {wifi.status}
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>

                  {/* Filter & Layer Controls Overlay */}
                  <div className="map-controls-overlay">
                    <div className="control-panel">
                      <div className="control-header">
                        <h6 className="mb-0">
                          <i className="fas fa-filter"></i> Filter & Layer Controls
                        </h6>
                        <button
                          className="btn btn-sm btn-outline-light ms-2"
                          type="button"
                          onClick={() => setPanelCollapsed(!panelCollapsed)}
                          aria-expanded={!panelCollapsed}
                        >
                          <i className={`fas fa-chevron-${panelCollapsed ? 'down' : 'up'}`}></i>
                        </button>
                      </div>
                      <div className={`control-content ${panelCollapsed ? 'collapsed' : ''}`}>
                        {/* Layer Controls */}
                        <div className="control-section">
                          <h6 className="section-title">
                            <i className="fas fa-layer-group"></i> Layer Visibility
                          </h6>
                          <div className="layer-controls">
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="showTowers"
                                checked={showTowers}
                                onChange={(e) => setShowTowers(e.target.checked)}
                              />
                              <label className="form-check-label text-light" htmlFor="showTowers">
                                <i className="fas fa-broadcast-tower text-primary"></i> Show Towers
                              </label>
                            </div>
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="showWifi"
                                checked={showWifi}
                                onChange={(e) => setShowWifi(e.target.checked)}
                              />
                              <label className="form-check-label text-light" htmlFor="showWifi">
                                <i className="fas fa-wifi text-success"></i> Show WiFi
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Tower Filters */}
                        <div className="control-section">
                          <h6 className="section-title">
                            <i className="fas fa-broadcast-tower"></i> Tower Filters
                          </h6>
                          <div className="filter-grid">
                            <div className="filter-item">
                              <select
                                className="form-select form-select-sm"
                                value={selectedOperator}
                                onChange={(e) => setSelectedOperator(e.target.value)}
                              >
                                <option value="">All Operators</option>
                                {operatorList.map(op => (
                                  <option key={op.id} value={op.nama}>{op.nama}</option>
                                ))}
                              </select>
                              <small className="text-muted">Operator</small>
                            </div>
                            <div className="filter-item">
                              <select
                                className="form-select form-select-sm"
                                value={selectedJenis}
                                onChange={(e) => setSelectedJenis(e.target.value)}
                              >
                                <option value="">All Types</option>
                                {jenisList.map(j => (
                                  <option key={j.id} value={j.nama}>{j.nama}</option>
                                ))}
                              </select>
                              <small className="text-muted">Jenis</small>
                            </div>
                            <div className="filter-item">
                              <select
                                className="form-select form-select-sm"
                                value={selectedKecamatan}
                                onChange={(e) => setSelectedKecamatan(e.target.value)}
                              >
                                <option value="">All Kecamatan</option>
                                {kecamatanList.map(k => (
                                  <option key={k.id} value={k.nama}>{k.nama}</option>
                                ))}
                              </select>
                              <small className="text-muted">Kecamatan</small>
                            </div>
                            <div className="filter-item">
                              <select
                                className="form-select form-select-sm"
                                value={selectedTowerStatus}
                                onChange={(e) => setSelectedTowerStatus(e.target.value)}
                              >
                                <option value="">All Status</option>
                                <option value="Aktif">Aktif</option>
                                <option value="Tidak Aktif">Tidak Aktif</option>
                              </select>
                              <small className="text-muted">Status</small>
                            </div>
                          </div>
                        </div>

                        {/* WiFi Filters */}
                        <div className="control-section">
                          <h6 className="section-title">
                            <i className="fas fa-wifi"></i> WiFi Filters
                          </h6>
                          <div className="filter-grid">
                            <div className="filter-item">
                              <select
                                className="form-select form-select-sm"
                                value={selectedProvider}
                                onChange={(e) => setSelectedProvider(e.target.value)}
                              >
                                <option value="">All Providers</option>
                                {providerList.map(p => (
                                  <option key={p.id} value={p.nama}>{p.nama}</option>
                                ))}
                              </select>
                              <small className="text-muted">Provider</small>
                            </div>
                            <div className="filter-item">
                              <select
                                className="form-select form-select-sm"
                                value={selectedWifiKecamatan}
                                onChange={(e) => setSelectedWifiKecamatan(e.target.value)}
                              >
                                <option value="">All Kecamatan</option>
                                {kecamatanList.map(k => (
                                  <option key={k.id} value={k.nama}>{k.nama}</option>
                                ))}
                              </select>
                              <small className="text-muted">Kecamatan</small>
                            </div>
                            <div className="filter-item">
                              <select
                                className="form-select form-select-sm"
                                value={selectedWifiStatus}
                                onChange={(e) => setSelectedWifiStatus(e.target.value)}
                              >
                                <option value="">All Status</option>
                                <option value="Aktif">Aktif</option>
                                <option value="Tidak Aktif">Tidak Aktif</option>
                              </select>
                              <small className="text-muted">Status</small>
                            </div>
                            <div className="filter-item">
                              <button
                                className="btn btn-outline-light btn-sm w-100"
                                onClick={() => {
                                  setSelectedOperator('');
                                  setSelectedJenis('');
                                  setSelectedKecamatan('');
                                  setSelectedTowerStatus('');
                                  setSelectedProvider('');
                                  setSelectedWifiKecamatan('');
                                  setSelectedWifiStatus('');
                                }}
                              >
                                <i className="fas fa-undo"></i> Reset
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Statistics Overlay */}
                  <div className="stats-overlay">
                    <div className="stats-item">
                      <i className="fas fa-broadcast-tower text-primary"></i>
                      <span>Menara: <span className="stats-number">{filteredTowers.length}</span></span>
                    </div>
                    <div className="stats-item">
                      <i className="fas fa-wifi text-success"></i>
                      <span>WiFi: <span className="stats-number">{filteredWifi.length}</span></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FooterOne />
    </div>
  );
};

export default LandingPage;